import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { NudosHoverText } from '../../../../components/NudosComponents';
import { ICountryDetail } from '../../../../types/countries';
import { BORD_CREDIT_PAYMENT_TYPES, IProductList, ORDER_STATUS, PAYMENT_TYPES } from '../../../../types/orders';
import { formatShortDate } from '../../../../utils/formatDatesAndHours';
import { formatRoundedUpNumber } from '../../../../utils/formatNumbers';
import { UploadPaymentLinkModal } from '..';
import {
  GoToDetailsArrow,
  StatusDelivered,
  StatusLate,
  StatusWhithDate,
  StatusDeliveryLate
} from '../../../../assets/images/orders';
import CheckOrAddCommentsButton from '../CheckOrAddCommentsButton/CheckOrAddCommentsButton';
import CheckOrAddCommentsModal from '../CheckOrAddCommentsModal/CheckOrAddCommentsModal';
import { IconInformation } from '../../../../assets/DesignSystem/SVGComponents';
import { IconPlatinum, IconPlatinumWhite } from '../../../../assets/images/services';
import { submitComment } from '../../../LogisticServices/utils/general.utils';
import { getOrderDetailedData } from '../../../../services/orders';
import { ICommentsAndObservations } from '../../../../types/global';
import { FooterDetailsRight } from './Components';
import WarehouseTemporaryStorageMarker from '../WarehouseTemporaryStorageMarker/WarehouseTemporaryStorageMarker';
import BordResponsibleUserPictures from '../../../../components/NudosComponents/BordReponsibleUser/BordReponsibleUserPictures/BordResponsibleUserPictures';
import useUsersState from '../../../../state/useUsersState';
import { PAYMENT_METHODS_LABELS } from '../../../../constants/constants';
import { IorganizationBusinessData } from 'types/bussines';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { getOrganizationData } from 'services/bussines';
import './OrderCardInListing.scss';

interface IorderCardInListing {
  data: IProductList;
  lockInternal?: boolean;
  refreshData?: () => void;
  redHeader?: boolean;
  specialCards?: boolean;
  withNotes?: boolean;
}

const OrderCardInListing = ({ data, refreshData, redHeader, specialCards, withNotes = true }: IorderCardInListing) => {
  const { users } = useUsersState();

  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState<boolean>(false);
  const [showCommentsModal, setShowCommentsModal] = useState<boolean>(false);
  const [loadingComments, setLoadingComments] = useState<boolean>(false);
  const [openTooltip, setOpenTooltip] = useState<boolean>(false);
  const [dataComments, setDataComments] = useState<ICommentsAndObservations[]>();
  const {
    orderId,
    organizationName,
    countryOrganization,
    statusName,
    paymentType,
    createdAt,
    cancelledDate,
    reasonToCancel = 'Sin información',
    deliveryDate,
    numberOfShipments = 0,
    countriesShipments,
    needInvoice,
    invoiceUrl,
    paymentLink,
    shimpentStatus,
    orderComments,
    editShipment,
    levelSubscription,
    responsibleIds,
    organizationId
  } = data;

  const resolvedReports = editShipment?.find(edit => edit.platform === 'NODI');

  const orderIsCanceled = statusName?.toLowerCase().trim() === 'cancelada';
  const orderAlreadyDelivered = statusName?.toLowerCase().trim() === 'entregada';
  const activeOrder = !orderIsCanceled && !orderAlreadyDelivered;
  const organizationFlag = countryOrganization?.flag || '';
  const numberOfShipmentsText = numberOfShipments === 1 ? 'envío' : 'envíos';
  const hasNudosLite = levelSubscription === 'Lite';
  const formattedCreationDateShort = createdAt ? formatShortDate(createdAt)?.onlyDate : 'Sin fecha';
  const formattedDeliveryDate = deliveryDate ? formatShortDate(deliveryDate)?.textWithTimezone : 'Sin fecha';
  const formattedCancelingDate = cancelledDate ? formatShortDate(cancelledDate)?.textWithTimezone : 'Sin fecha';
  const notNeedRenderCommentText = (data.orderTemporaryStorageReasons?.length ?? 0) > 0
  const notNeedRenderAdditionalConfigs = (data.orderTemporaryStorageReasons?.length ?? 0) <= 1;

  const responsibleUsersListFromState = users.filter(user => responsibleIds?.includes(user.id));

  const responsibleUsersList = responsibleUsersListFromState.map(user => ({
    id: user?.id,
    user: user,
    orderId: Number(orderId)
  }));

  const displayBordReponsibleUserPictures = statusName !== ORDER_STATUS.PENDING && !!responsibleUsersList?.length;

  const handleOpenOrCloseModal = () => setShowCommentsModal(!showCommentsModal);

  const getOrderStatusRelatedStyles = () => {
    if (orderIsCanceled) return 'canceled';
    if (redHeader) return 'backOrder';
    if (orderAlreadyDelivered) return 'delivered';
    return '';
  };

  const getShipmentFlags = () => {
    const shipmentsFlags = Array.isArray(countriesShipments)
      ? countriesShipments.map((shipmentCountry: ICountryDetail, index: number) => {
          return (
            <div
              key={`shipmentFlag-${index}-order-${orderId}`}
              className="flag"
              style={{ backgroundImage: `url("${shipmentCountry?.flag || ''}")` }}
            />
          );
        })
      : [];
    const firstFlags = shipmentsFlags.slice(0, 2);
    const excessFlags = shipmentsFlags.length > 2 ? shipmentsFlags.length - 2 : 0;
    const moreFlagsIndicator = (
      <div key={uuid()} className="flag moreFlags">
        +{excessFlags}
      </div>
    );
    if (Array.isArray(countriesShipments) && countriesShipments.length <= 2) return shipmentsFlags;
    else return [firstFlags, moreFlagsIndicator];
  };

  const getInvoiceSection = () => {
    if (invoiceUrl)
      return (
        <a className="invoice active" href={invoiceUrl} target="blank">
          Cargar factura ✅
        </a>
      );
    if (needInvoice) return <div className="invoice">Cargar factura ⚠️</div>;
    return <div className="invoice inactive">Cargar factura</div>;
  };

  const handleUploadPaymentLink = () => setShowPaymentLinkModal(true);

  const getOrganizationInfo = async (): Promise<IorganizationBusinessData | undefined> => {
    if (!organizationId) return;
    try {
      const response = await getOrganizationData(Number(organizationId));
      return response;
    } catch (error) {
      displayErrorNotification();
    }
  };
  const handleCommentsSubmission = async (comment: ICommentsAndObservations) => {
    if (!orderId) return;
    setLoadingComments(true);
    try {
      const orgInfo = await getOrganizationInfo();
      const kamUserId = Number(orgInfo?.assignedKam?.id);
      await submitComment({
        orderId,
        allowNewCommentsSubmission: !loadingComments,
        handleLoading: setLoadingComments,
        comments: comment.comments,
        mentions: comment.mentions || [],
        commentImage: comment?.commentImage,
        ...(kamUserId && { kamUserId: kamUserId })
      });
      const response = await getOrderDetailedData(orderId);
      setDataComments(response?.orderComments || []);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingComments(false);
    }
  };

  const formatPaymentType = (paymentType?: string) => {
    if (!paymentType) return '';
    const itIsBordCredit = BORD_CREDIT_PAYMENT_TYPES?.includes(paymentType as PAYMENT_TYPES);
    if (itIsBordCredit) return PAYMENT_METHODS_LABELS.CREDIT_BORD;
    return paymentType;
  };


  useEffect(() => {
    setDataComments(orderComments);
  }, [orderComments]);


  return (
    <div className={`orderCardInListing ${getOrderStatusRelatedStyles()}`}>
      {resolvedReports && openTooltip && (
        <div className="shippingUpdateAlert">
          <div className="textUpdateAlert">Se actualizó la información de algunos envíos</div>
        </div>
      )}
      {showPaymentLinkModal && (
        <UploadPaymentLinkModal
          successfullRequestCallback={refreshData}
          setShowPaymentLinkModal={setShowPaymentLinkModal}
          orderData={data}
          defaultLink={paymentLink || undefined}
        />
      )}
      <section className="headerSection">
        <div className="organizationNameAndFlag">
          <div className="flag" style={{ backgroundImage: `url(${organizationFlag})` }} />

          <NudosHoverText
            customClassName="organizationName"
            text={organizationName || 'Sin nombre de la organización'}
            charactersLimit={35}
            onlyIfTruncated
          />
          {hasNudosLite && (
            <div className="platinumContainer">{orderAlreadyDelivered ? <IconPlatinumWhite /> : <IconPlatinum />}</div>
          )}
        </div>

        {resolvedReports ? (
          <div
            className="orderNumberAndArrow"
            onMouseEnter={() => setOpenTooltip(true)}
            onMouseLeave={() => setOpenTooltip(false)}
          >
            <Link className="replaceRedirectionLink" to={`/order/details/${data.orderId}`} />
            <IconInformation />
            <div className="orderNumber orderNumberBlue">Orden N°{orderId}</div>
            <GoToDetailsArrow fill="#54A1FC" className="goToDetailsArrowBlue" />
          </div>
        ) : (
          <div className="orderNumberAndArrow">
            <Link className="replaceRedirectionLink" to={`/order/details/${data.orderId}`} />
            <div className="orderNumber">Orden N°{orderId}</div>
            <GoToDetailsArrow className="goToDetailsArrow" />
          </div>
        )}
      </section>
      <section className="bodySection">
        {specialCards && (
          <>
            {!!data?.deliveryDate && (
              <div className="floatDate">Entregado: {formatShortDate(data?.deliveryDate || '').onlyDate}</div>
            )}
            {!data.deliveryDate && data?.estimatedDeliveryDate && (
              <div className="floatDate">Fecha estimada: {formatShortDate(data?.estimatedDeliveryDate).onlyDate}</div>
            )}{' '}
          </>
        )}
        <div className="absoluteShimpments">
          {specialCards &&
            shimpentStatus &&
            shimpentStatus.length > 0 &&
            shimpentStatus.map(item => {
              return (
                <div className="itemShimpent" key={uuid()}>
                  {item.status === 'WITH_DATE' && (
                    <div className="whithDate">
                      <StatusWhithDate />
                    </div>
                  )}
                  {item.status === 'WITHOUT_DATE' && <div className="whithOutDate" />}
                  {item.status === 'DELIVERED' && (
                    <div className="delivered">
                      <StatusDelivered />
                    </div>
                  )}
                  {item.status === 'LATE' && (
                    <div className="late">
                      <StatusLate />
                    </div>
                  )}
                  {item.status === 'DELIVERY_LATE' && (
                    <div className="deliveryLate">
                      <StatusDeliveryLate />
                    </div>
                  )}
                </div>
              );
            })}
        </div>
        <div className="leftData">
          <div className="firstLineData">
            {activeOrder && (
              <div className="paymentData">
                <div className="paymentMethod" style={{ width: specialCards ? 'auto' : '' }}>
                  {formatPaymentType(paymentType || '')}
                </div>
                {!specialCards && (
                  <div className="paymentValue">
                    {`$${
                      ['USD', 'ARS', 'BRL'].includes(data?.currency || '')
                        ? formatRoundedUpNumber(Number(data?.totalUsd || 0), 2)
                        : formatRoundedUpNumber(Number(data?.total || 0), 2)
                    } ${['BRL', 'ARS'].includes(data?.currency || '') ? 'USD' : data?.currency}`}
                  </div>
                )}
                {specialCards && (
                  <>
                    <div className="numberOfShipments">
                      {numberOfShipments || 'Sin'} {numberOfShipmentsText}
                    </div>
                    {countriesShipments && <div className="shipmentDestinationFlags">{getShipmentFlags()}</div>}
                  </>
                )}
              </div>
            )}
            {orderIsCanceled && (
              <div className="canceledOrderData">
                <div className="cancelingReason">{reasonToCancel}</div>
                <div className="cancelingDate">Fecha de cancelación: {formattedCancelingDate}</div>
              </div>
            )}
            {orderAlreadyDelivered && (
              <div className="deliveredOrderData">
                <div className="deliveryDate">Entrega completa: {formattedDeliveryDate}</div>
              </div>
            )}
          </div>
          <div className="shipmentAndInvoiceData">
            {!specialCards && (
              <>
                <div className="shipmentData">
                  <div className="numberOfShipments">
                    {numberOfShipments || 'Sin'} {numberOfShipmentsText}
                  </div>
                  {countriesShipments && <div className="shipmentDestinationFlags">{getShipmentFlags()}</div>}
                </div>
                {!specialCards && <div className="invoiceData">{activeOrder && getInvoiceSection()}</div>}
              </>
            )}
          </div>
        </div>
        <div className="rightData">
          <div className="creationDate">{formattedCreationDateShort}</div>
          {displayBordReponsibleUserPictures ? (
            <BordResponsibleUserPictures responsibleUsersList={responsibleUsersList} dropdownDirection="left" />
          ) : (
            <FooterDetailsRight
              orderData={data}
              showComponent={!specialCards}
              handleUploadPaymentLink={handleUploadPaymentLink}
              notNeedRenderAdditionalConfigs={notNeedRenderAdditionalConfigs}
            />
          )}
        </div>
      </section>
      {withNotes && (
        <CheckOrAddCommentsButton
          openModal={handleOpenOrCloseModal}
          notNeedRenderCommentText={notNeedRenderCommentText}
        />
      )}
      <WarehouseTemporaryStorageMarker orderTemporaryStorageReasons={data.orderTemporaryStorageReasons || []} />
      {showCommentsModal && !!organizationId && (
        <CheckOrAddCommentsModal
          closeModal={handleOpenOrCloseModal}
          orderComments={dataComments}
          handleSubmitComments={handleCommentsSubmission}
          loading={loadingComments}
          responsibleUsers={responsibleUsersList}
          organizationId={organizationId}
        />
      )}
    </div>
  );
};

export default OrderCardInListing;
